import { SignalStoreBuilder } from "./SignalStoreBuilder";
import { BatteryDetailSnapshot } from "../_models/BatteryDetailSnapshot";

type BatterySnapshotMap = {
  id_battery: number;
  /** Latest snapshot first; feeds the headline values. */
  snapshots: BatteryDetailSnapshot[];
  /** Selected time window, oldest → newest; feeds the charts and alarm timeline. */
  history: BatteryDetailSnapshot[];
  isRefreshing: boolean;
};

export const SignalBatteryDetailSnapshotStore = new SignalStoreBuilder<
  BatterySnapshotMap,
  {}
>(
  (snapshot) => snapshot.id_battery,
  (json) => {
    const id = json["id_battery"] as number;
    const snapshots = (json["snapshots"] as any[]).map((s) =>
      BatteryDetailSnapshot.fromJson(s),
    );

    return {
      id_battery: id,
      snapshots,
      history: [],
      isRefreshing: false,
    };
  },
)
  .withUpdate((id) => `batteries/${id}/snapshots`)
  .withMethod("loadAllLatest", (_store, http, { upsert }) => async () => {
    const response = await http.get("batteries/snapshots", {
      hideLoader: true,
    });
    if (!response) return;
    for (const item of response as any as {
      id_battery: number;
      snapshot: unknown;
    }[]) {
      if (!item.snapshot) continue;
      const incoming = BatteryDetailSnapshot.fromJson(item.snapshot);
      const existing = _store.entityMap()[item.id_battery]?.snapshots ?? [];
      upsert({
        id_battery: item.id_battery,
        snapshots: [incoming, ...existing.filter(s => s.id_snapshot !== incoming.id_snapshot)],
        history: _store.entityMap()[item.id_battery]?.history ?? [],
        isRefreshing: false,
      });
    }
  })
  .withMethod(
    "loadLatest",
    (_store, http, { upsert }) =>
      async (id_battery: number) => {
        upsert({
          id_battery,
          snapshots: _store.entityMap()[id_battery]?.snapshots ?? [],
          history: _store.entityMap()[id_battery]?.history ?? [],
          isRefreshing: true,
        });
        const response = await http.get(`batteries/${id_battery}/snapshot`, {
          hideLoader: true,
        });
        if (!response) {
          upsert({
            id_battery,
            snapshots: _store.entityMap()[id_battery]?.snapshots ?? [],
            history: _store.entityMap()[id_battery]?.history ?? [],
            isRefreshing: false,
          });
          return;
        }
        const incoming = BatteryDetailSnapshot.fromJson(response);
        const existing = _store.entityMap()[id_battery]?.snapshots ?? [];
        upsert({
          id_battery,
          snapshots: [incoming, ...existing.filter(s => s.id_snapshot !== incoming.id_snapshot)],
          history: _store.entityMap()[id_battery]?.history ?? [],
          isRefreshing: false,
        });
      },
  )
  .withMethod(
    "loadHistory",
    (_store, http, { upsert }) =>
      async (
        id_battery: number,
        params: { from?: string; to?: string; limit?: number } = {},
      ) => {
        const keep = () => ({
          id_battery,
          snapshots: _store.entityMap()[id_battery]?.snapshots ?? [],
          history: _store.entityMap()[id_battery]?.history ?? [],
        });
        upsert({ ...keep(), isRefreshing: true });
        const response = await http.get(
          `batteries/${id_battery}/snapshots`,
          params,
          true,
        );
        if (!response) {
          upsert({ ...keep(), isRefreshing: false });
          return;
        }
        // The API answers newest first; charts and the timeline want oldest first.
        const history = (response["snapshots"] as unknown[])
          .map(BatteryDetailSnapshot.fromJson)
          .reverse();
        upsert({ ...keep(), history, isRefreshing: false });
      },
  )
  .withAutoRefresh((store) => store.loadAllLatest())
  .providedInRoot()
  .build();
